import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { notificationApi } from '../api/notificationApi';
import { connectNotificationSocket, type NotificationSocketHandle } from '../api/notificationSocket';
import type { NotificationItem } from '../types/notification.types';
import { useAuth } from './AuthContext';

type NotificationContextValue = {
  unreadCount: number;
  recent: NotificationItem[];
  refresh: () => Promise<void>;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
};

const NotificationContext = createContext<NotificationContextValue | undefined>(undefined);

export const NotificationProvider = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [recent, setRecent] = useState<NotificationItem[]>([]);
  const handleRef = useRef<NotificationSocketHandle | null>(null);

  const refresh = useCallback(async () => {
    try {
      const [list, count] = await Promise.all([
        notificationApi.getNotifications({ limit: 10 }),
        notificationApi.getUnreadCount(),
      ]);
      setRecent(list.data);
      setUnreadCount(count.data.count);
    } catch {
      /* ignore — bell stays at previous value */
    }
  }, []);

  const markRead = useCallback(async (id: string) => {
    try {
      await notificationApi.markRead(id);
      setRecent((prev) => prev.map((n) => n.id === id ? { ...n, is_read: true } : n));
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch {
      /* noop */
    }
  }, []);

  const markAllRead = useCallback(async () => {
    try {
      await notificationApi.markAllRead();
      setRecent((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch {
      /* noop */
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setUnreadCount(0);
      setRecent([]);
      handleRef.current?.close();
      handleRef.current = null;
      return;
    }
    void refresh();
    const handle = connectNotificationSocket((event) => {
      if (event.event === 'notification_created') {
        setRecent((prev) => [event.data, ...prev].slice(0, 10));
        setUnreadCount((c) => c + 1);
      } else if (event.event === 'unread_count_updated') {
        setUnreadCount(event.data.count);
      }
    });
    handleRef.current = handle;
    return () => {
      handle.close();
      if (handleRef.current === handle) handleRef.current = null;
    };
  }, [isAuthenticated, refresh]);

  const value = useMemo<NotificationContextValue>(() => ({
    unreadCount, recent, refresh, markRead, markAllRead,
  }), [unreadCount, recent, refresh, markRead, markAllRead]);

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
};

export const useNotifications = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotifications must be used inside NotificationProvider');
  return ctx;
};
